import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, ShieldCheck, Heart, MapPin } from "lucide-react";
import { site } from "@/config/site";
import { Seo } from "@/components/Seo";
import { JsonLd } from "@/components/JsonLd";
import { LocalBusinessJsonLd } from "@/components/LocalBusinessJsonLd";
import { PageHero } from "@/components/PageHero";
import { SectionHeading } from "@/components/SectionHeading";
import { ServiceCard } from "@/components/ServiceCard";
import { Faq } from "@/components/Faq";
import { MapSection } from "@/components/MapSection";
import { CtaBlock } from "@/components/CtaBlock";
import { WhatsAppButton, CallButton } from "@/components/CtaButtons";

const why = [
  { icon: Sparkles, title: "Kişiye Özel Uygulama", text: "Seans öncesi kısa görüşme; baskı ve odak size göre ayarlanır." },
  { icon: ShieldCheck, title: "Hijyen", text: "Her misafir için yeni tekstil, havalandırılmış ve temizlenmiş oda." },
  { icon: Heart, title: "Mahremiyet", text: "Kapalı, sakin odalar; yalnızca çalışılan bölge açıkta kalır." },
  { icon: MapPin, title: "Merkezi Konum", text: `${site.address.street}, ${site.address.locality}. ${site.openingHours.text}.` },
];

export default function LandingPage({ page }) {
  const serviceLd = page.service && {
    "@context": "https://schema.org",
    "@type": "Service",
    name: page.service,
    serviceType: page.service,
    url: `${site.siteUrl}${page.path}`,
    image: `${site.siteUrl}${page.image}`,
    provider: { "@type": "HealthAndBeautyBusiness", "@id": `${site.siteUrl}/#business`, name: site.name, telephone: site.phoneE164 },
    areaServed: ["Bursa", "Nilüfer"].map((name) => ({ "@type": "Place", name })),
  };

  return (
    <>
      <Seo title={page.title} description={page.description} path={page.path} image={page.image} />
      <LocalBusinessJsonLd />
      {serviceLd && <JsonLd id="service" data={serviceLd} />}
      <PageHero
        eyebrow={page.eyebrow}
        accent={page.accent}
        title={page.h1}
        text={page.intro}
        crumbs={[{ label: "Ana Sayfa", path: "/" }, { label: page.crumb, path: page.path }]}
        testId="landing-hero"
      />

      <section className="section bg-white" data-testid="landing-content">
        <div className="container-wide grid gap-12 lg:grid-cols-12">
          <article className="lg:col-span-8 space-y-12">
            {page.sections.map((s, i) => (
              <div key={s.h2} className="grid gap-4 sm:grid-cols-[3.5rem_1fr]">
                <span className="hidden sm:block text-3xl font-extrabold gold-text leading-none pt-1">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <h2 className="text-2xl md:text-3xl font-bold text-ink leading-tight">{s.h2}</h2>
                  {s.paragraphs.map((p) => <p key={p.slice(0, 30)} className="mt-4 text-muted leading-relaxed">{p}</p>)}
                  {s.list && (
                    <ul className="mt-4 space-y-2">
                      {s.list.map((li) => (
                        <li key={li} className="flex gap-3 text-sm text-ink/80"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-gold shrink-0" aria-hidden="true" />{li}</li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            ))}
          </article>
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28 self-start">
            <img src={page.image} alt={page.imageAlt} width="1200" height="800" loading="lazy" decoding="async" className="rounded-2xl shadow-xl aspect-[3/2] object-cover" />
            <div className="card p-6 emerald-gradient text-white border-0">
              <p className="eyebrow">Randevu</p>
              <p className="mt-2 text-lg font-bold">{site.openingHours.text}</p>
              <p className="mt-1 text-sm text-white/75">{site.priceNote}</p>
              <div className="mt-5 flex flex-col gap-2">
                <WhatsAppButton testId="landing-aside-whatsapp" />
                <CallButton className="btn-gold" label="Hemen Ara" testId="landing-aside-call" />
                <Link to="/iletisim" className="btn btn-ghost-light" data-testid="landing-aside-appointment">Randevu Al</Link>
              </div>
            </div>
            <nav className="card p-6" aria-label="İlgili sayfalar" data-testid="related-links">
              <h2 className="font-bold text-base mb-3">İlgili Sayfalar</h2>
              <ul className="space-y-2">
                {page.related.map((r) => (
                  <li key={r.path}>
                    <Link to={r.path} className="inline-flex items-center gap-1 text-sm text-emerald hover:text-gold transition-colors">
                      <ArrowRight size={14} aria-hidden="true" /> {r.label}
                    </Link>
                  </li>
                ))}
                <li><Link to="/" className="inline-flex items-center gap-1 text-sm text-emerald hover:text-gold transition-colors"><ArrowRight size={14} aria-hidden="true" /> Ana Sayfa</Link></li>
              </ul>
            </nav>
          </aside>
        </div>
      </section>

      <section className="section bg-offwhite" aria-labelledby="landing-why-title" data-testid="landing-why">
        <div className="container-wide">
          <SectionHeading eyebrow="Neden Lavenza" accent="Lavenza" title="Farkı" id="landing-why-title" />
          <ul className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {why.map(({ icon: Icon, title, text }) => (
              <li key={title} className="card p-6 hover-lift">
                <div className="icon-box mb-4"><Icon className="w-6 h-6" aria-hidden="true" /></div>
                <h3 className="font-bold text-lg mb-1">{title}</h3>
                <p className="text-sm text-muted leading-relaxed">{text}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="section bg-white" aria-labelledby="landing-services-title" data-testid="landing-services">
        <div className="container-wide">
          <SectionHeading eyebrow="Hizmetlerimiz" accent="Masaj" title="Seçenekleri" lead={site.priceNote} id="landing-services-title" />
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {site.services.map((s, i) => <ServiceCard key={s.slug} service={s} index={i} />)}
          </div>
        </div>
      </section>

      <Faq items={page.faq} title="Sık Sorulan Sorular" lead="Bu sayfaya özel kısa yanıtlar" />
      <MapSection />
      <CtaBlock />
    </>
  );
}
